import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, AlertCircle, Trash2, RefreshCcw, Info, Clock, Globe, Bell } from 'lucide-react';
import logger from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOW_OPTIONS = [
  { value: 1, label: 'Last hour' },
  { value: 24, label: 'Last 24 hours' },
  { value: 24 * 7, label: 'Last 7 days' },
  { value: 24 * 30, label: 'Last 30 days' },
];

function _relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function _hostOf(url) {
  try { return new URL(url).pathname || url; } catch { return url || ''; }
}

export default function ClientErrorsTab() {
  const [errors, setErrors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [since, setSince] = useState(24);
  const [expanded, setExpanded] = useState(null);
  // Spike-alert trigger state — super-admin manual fire-drill button.
  const [spikeBusy, setSpikeBusy] = useState(false);
  const [lastSpike, setLastSpike] = useState(null);

  const triggerSpikeAlert = async () => {
    setSpikeBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/errors/check-spike`,
        null,
        { withCredentials: true },
      );
      const data = res.data || {};
      setLastSpike(data);
      const status = data.status;
      if (status === 'alerted') {
        toast.success(`Alert sent to ${data.recipients} super-admin${data.recipients === 1 ? '' : 's'} (${data.distinct} distinct stacks).`);
      } else if (status === 'below_threshold') {
        toast(`Below threshold — only ${data.distinct} distinct error${data.distinct === 1 ? '' : 's'} in the last 10 min (need ≥5).`, { icon: 'ℹ️' });
      } else if (status === 'no_recipients') {
        toast.error('No super-admin recipients configured.');
      } else if (status === 'send_failed') {
        toast.error('Resend send failed — check backend logs.');
      } else if (status === 'cooldown') {
        toast(`In cooldown — next alert in ~${data.minutes_until_next}m.`, { icon: '⏳' });
      } else {
        toast(`Status: ${status || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Spike check failed');
      logger.error('ClientErrorsTab: spike trigger failed', e);
    } finally {
      setSpikeBusy(false);
    }
  };

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/errors/client`,
        { params: { since_hours: since, limit: 200 }, withCredentials: true },
      );
      setErrors(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load client errors');
      logger.error('ClientErrorsTab: fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [since]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  const handleDelete = async (error_id) => {
    if (!window.confirm('Clear this error from the log?')) return;
    try {
      await axios.delete(
        `${API_URL}/api/errors/client/${error_id}`,
        { withCredentials: true },
      );
      setErrors(list => list.filter(e => e.error_id !== error_id));
      setTotal(t => Math.max(0, t - 1));
      toast.success('Error cleared');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear error');
    }
  };

  const sortedErrors = useMemo(
    () => [...errors].sort((a, b) => (b.count || 1) - (a.count || 1)),
    [errors],
  );
  const occurrences = useMemo(
    () => errors.reduce((sum, e) => sum + (e.count || 1), 0),
    [errors],
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading client errors…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-errors-tab">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-[#0F2040] text-base">
                <AlertCircle className="w-4 h-4 text-[#E57373]" />
                Client-Side Errors
              </CardTitle>
              <div className="text-[11px] text-slate-500 flex items-start gap-1.5 mt-1">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
                Every JavaScript error thrown in production is reported to
                /api/errors/client. Identical stacks are de-duped over a
                60-minute window; the count is the number of times the same
                error fired from the same IP before a new row was created.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Select value={String(since)} onValueChange={v => setSince(parseInt(v, 10))}>
                <SelectTrigger className="h-8 w-[140px] text-xs"
                               data-testid="client-errors-window-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOW_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={String(o.value)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={fetchErrors}
                className="h-8 text-[11px]" data-testid="client-errors-refresh">
                <RefreshCcw className="w-3 h-3 mr-1" />
                Refresh
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={triggerSpikeAlert}
                disabled={spikeBusy}
                className="h-8 text-[11px] border-amber-300 hover:border-amber-400 hover:bg-amber-50 text-amber-800"
                data-testid="client-errors-trigger-spike-btn"
                title="Run the spike check now (force=true). Bypasses the 60-min cooldown so you can verify the email pipeline."
              >
                {spikeBusy ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Bell className="w-3 h-3 mr-1" />
                )}
                Test alert
              </Button>
            </div>
          </div>
          {lastSpike && (
            <div
              className="text-[10px] text-slate-500 mt-2 font-mono"
              data-testid="client-errors-spike-result"
            >
              Last check: <span className="font-semibold text-slate-700">{lastSpike.status}</span>
              {typeof lastSpike.distinct === 'number' ? ` · ${lastSpike.distinct} distinct` : ''}
              {typeof lastSpike.recipients === 'number' ? ` · ${lastSpike.recipients} recipients` : ''}
              {typeof lastSpike.minutes_until_next === 'number' ? ` · cooldown ~${lastSpike.minutes_until_next}m` : ''}
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-3 text-[11px] text-slate-500 border-b border-slate-100 pb-2">
            <span><strong className="text-slate-700">{total}</strong> distinct errors</span>
            <span className="text-slate-300">•</span>
            <span><strong className="text-slate-700">{occurrences}</strong> total occurrences</span>
          </div>

          {sortedErrors.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400"
                 data-testid="client-errors-empty">
              No errors reported in this window. 🎉
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="client-errors-list">
              {sortedErrors.map(err => {
                const isOpen = expanded === err.error_id;
                return (
                  <div key={err.error_id}
                       className="border border-slate-200 rounded-md bg-white overflow-hidden"
                       data-testid={`client-error-row-${err.error_id}`}>
                    <div className="flex items-start gap-2 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : err.error_id)}
                        className="flex-1 text-left min-w-0"
                        data-testid={`client-error-toggle-${err.error_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 rounded bg-rose-50 text-rose-700 text-[10px] font-bold">
                            ×{err.count || 1}
                          </span>
                          <span className="text-[12px] text-slate-900 font-medium truncate">
                            {err.message}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {_relativeTime(err.last_seen_at || err.occurred_at)}
                          </span>
                          {err.url && (
                            <span className="flex items-center gap-1 truncate max-w-[300px]">
                              <Globe className="w-3 h-3" />
                              {_hostOf(err.url)}
                            </span>
                          )}
                          {err.user_email && (
                            <span className="truncate">
                              {err.user_email}
                            </span>
                          )}
                          <span className="font-mono text-slate-400">
                            #{err.stack_hash}
                          </span>
                        </div>
                      </button>
                      <Button size="sm" variant="ghost"
                        onClick={() => handleDelete(err.error_id)}
                        className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                        data-testid={`client-error-delete-${err.error_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    {isOpen && (
                      <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 space-y-2"
                           data-testid={`client-error-detail-${err.error_id}`}>
                        {err.stack && (
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                              Stack
                            </div>
                            <pre className="text-[10px] font-mono text-slate-700 whitespace-pre-wrap break-all max-h-64 overflow-auto">
                              {err.stack}
                            </pre>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3 text-[10px]">
                          <div>
                            <span className="font-semibold text-slate-500">First seen:</span>{' '}
                            <span className="text-slate-700">{new Date(err.occurred_at).toLocaleString()}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-500">Last seen:</span>{' '}
                            <span className="text-slate-700">{new Date(err.last_seen_at || err.occurred_at).toLocaleString()}</span>
                          </div>
                          <div className="col-span-2 truncate">
                            <span className="font-semibold text-slate-500">URL:</span>{' '}
                            <span className="text-slate-700 font-mono break-all">{err.url || '—'}</span>
                          </div>
                          <div className="col-span-2 truncate">
                            <span className="font-semibold text-slate-500">User Agent:</span>{' '}
                            <span className="text-slate-600">{err.user_agent || '—'}</span>
                          </div>
                          {err.context && Object.keys(err.context).length > 0 && (
                            <div className="col-span-2">
                              <div className="font-semibold text-slate-500 mb-0.5">Context</div>
                              <pre className="text-[10px] font-mono text-slate-700 whitespace-pre-wrap break-all max-h-32 overflow-auto bg-white border border-slate-200 rounded p-1.5">
                                {JSON.stringify(err.context, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
